import React, { useMemo, useCallback } from 'react';
import classNames from 'classnames';
import { Text } from '@dropbox/dig-components/typography';
import { defineMessages, useIntl } from 'react-intl';
import { getBrandName } from 'js/sign-components/common/brand';
import HfReactHelper from 'js/sign-components/common/hf-react-helper';
import styles from './index.scss';
import { getCouponPromotionalPeriodCount } from '../coupon-duration';
import { isAnnualTimePeriod, isMonthlyTimePeriod } from '../pricing-period';
import {
  shouldShowPromotionalAnnualRate,
  shouldShowPromotionalMonthlyRate,
} from './coupon-rate-text';
import {
  getFeatureData,
  getFeatureRows,
  getValidBillingPlans,
  isFreePlan,
} from './helpers';
import { getPlanHeaderProps } from './get-plan-header-props';
import { isCurrentPlan, type PlanCtaContext } from './plan-cta';
import PricingFeatureGrid from './pricing-feature-grid';
import PricingPlanHeader from './pricing-plan-header';
import { PricingPlanPickers } from './pricing-plan-picker';
import { usePricingPlanColumns } from './use-pricing-plan-columns';
import type { BillingPlan, PricingLevelsProps } from '../types';

const messages = defineMessages({
  freeNoMonthlyPayAsYouGo: {
    id: 'pricing.freeNoMonthlyPayAsYouGo',
    description: 'Pricing copy for the free tier on the pricing page',
    defaultMessage: 'No monthly pay as you go',
  },
  discountedPeriod: {
    id: 'pricing.discountedPeriod',
    description:
      'Pricing period label for a limited-time promotional monthly price',
    defaultMessage: '{count, plural, one {first month} other {first # months}}',
  },
  discountedAnnualPeriod: {
    id: 'pricing.discountedAnnualPeriod',
    description:
      'Pricing period label for a limited-time promotional annual price',
    defaultMessage: '{count, plural, one {first year} other {first # years}}',
  },
  pricePerMonth: {
    id: 'pricing.pricePerMonth',
    description: 'Pricing copy showing a monthly price',
    defaultMessage: '{price} / mo',
  },
  afterDiscountedPeriod: {
    id: 'pricing.afterDiscountedPeriod',
    description:
      'Pricing copy describing the recurring monthly price after a promotional period',
    defaultMessage: 'then {price} per month',
  },
  afterDiscountedAnnualPeriod: {
    id: 'pricing.afterDiscountedAnnualPeriod',
    description:
      'Pricing copy describing the recurring annual price after a promotional period',
    defaultMessage: 'then {price} billed yearly',
  },
  buttonFreeTrial: {
    id: 'pricing.buttonFreeTrial',
    description: 'Button text to start a free trial',
    defaultMessage: 'Start free trial',
  },
  buttonGetStarted: {
    id: 'pricing.buttonGetStarted',
    description: 'Button text to get started with a plan',
    defaultMessage: 'Get started',
  },
  buttonCurrentPlan: {
    id: 'pricing.buttonCurrentPlan',
    description: 'Button text identifying the current paid plan',
    defaultMessage: 'Current plan',
  },
  buttonUpgrade: {
    id: 'pricing.buttonUpgrade',
    description: 'Button text to upgrade to a higher paid plan',
    defaultMessage: 'Upgrade',
  },
  buttonDowngrade: {
    id: 'pricing.buttonDowngrade',
    description: 'Button text to downgrade to a lower paid plan',
    defaultMessage: 'Downgrade',
  },
  packageFeaturesTitle: {
    id: 'pricing.packageFeaturesTitle',
    description: 'Section heading above the plan feature comparison rows',
    defaultMessage: '{brandName} package features',
  },
});

type RateTextContext = {
  intl: ReturnType<typeof useIntl>;
  timePeriod: PricingLevelsProps['timePeriod'];
  couponNumMonths?: number;
};

function renderPromotionalMonthlyRate(
  plan: BillingPlan,
  { intl, couponNumMonths, timePeriod }: RateTextContext,
) {
  const count = getCouponPromotionalPeriodCount(couponNumMonths, timePeriod);
  return (
    <React.Fragment>
      <Text variant="paragraph" size="small" tagName="p" isBold={true}>
        {plan.formatted_coupon_monthly_price}
      </Text>
      <Text variant="paragraph" size="small" tagName="p" color="subtle">
        {intl.formatMessage(messages.discountedPeriod, { count })}
      </Text>
      <Text variant="paragraph" size="small" tagName="p" color="subtle">
        {intl.formatMessage(messages.afterDiscountedPeriod, {
          price: plan.formatted_rate_monthly,
        })}
      </Text>
    </React.Fragment>
  );
}

function renderStandardMonthlyRate(plan: BillingPlan) {
  return (
    <Text variant="paragraph" size="small" tagName="p" isBold={true}>
      {plan.formatted_rate_monthly} / mo
    </Text>
  );
}

function renderPromotionalAnnualRate(
  plan: BillingPlan,
  { intl, couponNumMonths, timePeriod }: RateTextContext,
) {
  const count = getCouponPromotionalPeriodCount(couponNumMonths, timePeriod);
  return (
    <React.Fragment>
      <Text variant="paragraph" size="small" tagName="p" isBold={true}>
        {intl.formatMessage(messages.pricePerMonth, {
          price: plan.formatted_coupon_annual_price_per_month,
        })}
      </Text>
      <Text variant="paragraph" size="small" tagName="p" color="subtle">
        {intl.formatMessage(messages.discountedAnnualPeriod, { count })}
      </Text>
      <Text variant="paragraph" size="small" tagName="p" color="subtle">
        {intl.formatMessage(messages.afterDiscountedAnnualPeriod, {
          price: plan.formatted_rate_annual,
        })}
      </Text>
    </React.Fragment>
  );
}

function renderStandardAnnualRate(plan: BillingPlan) {
  return (
    <React.Fragment>
      <Text variant="paragraph" size="small" tagName="p" isBold={true}>
        {plan.formatted_rate_annual_per_month} / mo
      </Text>
      <Text variant="paragraph" size="small" tagName="p" color="subtle">
        {plan.formatted_rate_annual} billed yearly
      </Text>
    </React.Fragment>
  );
}

function renderPaidPlanRate(
  plan: BillingPlan,
  context: RateTextContext,
  showPromotionalMonthlyRate: boolean,
  showPromotionalAnnualRate: boolean,
) {
  if (isMonthlyTimePeriod(context.timePeriod)) {
    if (showPromotionalMonthlyRate) {
      return renderPromotionalMonthlyRate(plan, context);
    }
    return renderStandardMonthlyRate(plan);
  }

  if (showPromotionalAnnualRate) {
    return renderPromotionalAnnualRate(plan, context);
  }

  return renderStandardAnnualRate(plan);
}

const PricingLevels: React.FC<PricingLevelsProps> = (props) => {
  const intl = useIntl();
  const isHelloSign = HfReactHelper.isHelloSign();
  const { isCompact } = props;

  const validBillingPlans = useMemo(
    () => getValidBillingPlans(props.billingPlans),
    [props.billingPlans],
  );

  const {
    planColumns,
    planCount,
    leftPlanIndex,
    rightPlanIndex,
    setLeftPlanIndex,
    setRightPlanIndex,
  } = usePricingPlanColumns(isCompact, validBillingPlans);

  const {
    featureChecks,
    featureNumbers,
    featureCheckNames,
    featureNumberNames,
  } = useMemo(() => getFeatureData(validBillingPlans), [validBillingPlans]);

  const featureRows = useMemo(
    () =>
      getFeatureRows(
        validBillingPlans,
        featureNumberNames,
        featureCheckNames,
        featureNumbers,
        featureChecks,
      ),
    [
      validBillingPlans,
      featureNumberNames,
      featureCheckNames,
      featureNumbers,
      featureChecks,
    ],
  );

  const hasPaidCurrentPlan = useMemo(
    () =>
      validBillingPlans.some(
        (plan) => isCurrentPlan(plan) && !isFreePlan(plan),
      ),
    [validBillingPlans],
  );

  const planCtaContext: PlanCtaContext = useMemo(
    () => ({
      timePeriod: props.timePeriod,
      hasValidCoupon: props.hasValidCoupon,
      canDirectPurchase: props.canDirectPurchase,
      isHelloSign,
      isLoggedIn: props.isLoggedIn,
      hasPaidCurrentPlan,
      isFreeTrialEligible: props.isFreeTrialEligible,
      canChangePlan: !props.changePlanRestricted,
    }),
    [
      props.timePeriod,
      props.hasValidCoupon,
      props.canDirectPurchase,
      isHelloSign,
      props.isLoggedIn,
      hasPaidCurrentPlan,
      props.isFreeTrialEligible,
      props.changePlanRestricted,
    ],
  );

  const planHeaderLabels = useMemo(
    () => ({
      getStartedLabel: intl.formatMessage(messages.buttonGetStarted),
      freeTrialLabel: intl.formatMessage(messages.buttonFreeTrial),
      currentPlanLabel: intl.formatMessage(messages.buttonCurrentPlan),
      upgradeLabel: intl.formatMessage(messages.buttonUpgrade),
      downgradeLabel: intl.formatMessage(messages.buttonDowngrade),
    }),
    [intl],
  );

  const getRateText = useCallback(
    (plan: BillingPlan) => {
      if (isFreePlan(plan)) {
        if (!isHelloSign) {
          return (
            <Text variant="paragraph" size="small" tagName="p" isBold={true}>
              {intl.formatMessage(messages.freeNoMonthlyPayAsYouGo)}
            </Text>
          );
        }

        return (
          <Text variant="paragraph" size="small" tagName="p" isBold={true}>
            <br />
            {isAnnualTimePeriod(props.timePeriod) ? <br /> : null}
          </Text>
        );
      }

      const hasValidCoupon = Boolean(props.hasValidCoupon);

      const showPromotionalMonthlyRate = shouldShowPromotionalMonthlyRate(
        props.timePeriod,
        isHelloSign,
        plan,
        hasValidCoupon,
      );
      const showPromotionalAnnualRate = shouldShowPromotionalAnnualRate(
        props.timePeriod,
        isHelloSign,
        plan,
        hasValidCoupon,
      );
      return renderPaidPlanRate(
        plan,
        {
          intl,
          timePeriod: props.timePeriod,
          couponNumMonths: props.couponNumMonths,
        },
        showPromotionalMonthlyRate,
        showPromotionalAnnualRate,
      );
    },
    [
      intl,
      isHelloSign,
      props.couponNumMonths,
      props.hasValidCoupon,
      props.timePeriod,
    ],
  );

  const desktopGridStyle = {
    '--pricing-plan-count': planColumns.length,
  } as React.CSSProperties;

  const brandName = getBrandName(isHelloSign ? 'S' : 'F');

  const packageFeaturesTitle = (
    <Text
      variant="label"
      size="large"
      tagName="div"
      className={styles.featuresSectionTitle}
    >
      {intl.formatMessage(messages.packageFeaturesTitle, { brandName })}
    </Text>
  );

  const renderPlanHeader = (column: (typeof planColumns)[number]) => {
    const planContext = {
      ...planCtaContext,
      isLoggedIn: Boolean(
        column.plan.is_logged_in || planCtaContext.isLoggedIn,
      ),
      isFreeTrialEligible:
        column.plan.is_free_trial_eligible ??
        planCtaContext.isFreeTrialEligible,
    };

    const headerProps = getPlanHeaderProps(
      column.plan,
      planContext,
      planHeaderLabels,
      getRateText(column.plan),
    );

    return (
      <PricingPlanHeader
        key={column.columnKey}
        plan={column.plan}
        planIndex={column.planIndex}
        columnSlot={column.columnSlot}
        isCompact={isCompact}
        {...headerProps}
      />
    );
  };

  return (
    <div
      className={classNames(styles.pricingLevels, {
        [styles.compact]: isCompact,
      })}
      role="region"
      aria-label="Pricing plan comparison"
    >
      {isCompact && props.billingToggle && (
        <div className={styles.compactBillingRow}>{props.billingToggle}</div>
      )}
      {isCompact ? (
        <div className={styles.compactComparison}>
          <div className={styles.compactPlanSummary}>
            {planCount > 0 && (
              <PricingPlanPickers
                plans={validBillingPlans}
                leftPlanIndex={leftPlanIndex}
                rightPlanIndex={rightPlanIndex}
                onLeftPlanChange={setLeftPlanIndex}
                onRightPlanChange={setRightPlanIndex}
              />
            )}
            {planColumns.map(renderPlanHeader)}
          </div>
          <PricingFeatureGrid
            className={classNames(
              styles.pricingGrid,
              styles.compactFeaturesGrid,
            )}
            planColumns={planColumns}
            featureRows={featureRows}
            featuresSectionTitle={packageFeaturesTitle}
          />
        </div>
      ) : (
        <div
          className={styles.pricingGrid}
          style={desktopGridStyle}
          role="region"
          aria-label="Pricing plan comparison"
        >
          <div className={styles.labelCorner} aria-hidden="true" />
          {planColumns.map(renderPlanHeader)}
          <PricingFeatureGrid
            embeddedInParentGrid
            planColumns={planColumns}
            featureRows={featureRows}
            featuresSectionTitle={packageFeaturesTitle}
          />
        </div>
      )}
    </div>
  );
};

export default PricingLevels;
